<template>
  <screen-swiper @swiped-right="nextLesson" @swiped-left="prevLesson" :left="canGoBack" right>
    <vv-screen-wrapper>
      <div class="screen-container" v-if="lesson">
        <div class="boxTitle" v-if="$mq == 'desktop'">
          <div class="leftButton">
            <div class="backButton" @click="$router.back()">
              <img src="@/assets/next.svg" alt="" />
            </div>
            <div class="title">
              {{ theme ? theme.title : '' }}
            </div>
          </div>
          <div class="buttonClass">
            <div @click="$router.push({ name: 'qrcode' })" class="qrcodeBtn">
              <img src="@/assets/qrcodelogo.png" />
            </div>
            <div @click="$router.push({ name: 'geoloc' })" class="geolocBtn" v-show="haveGeoloc">
              <img src="@/assets/geoloclogo.png" />
            </div>
          </div>
        </div>
        <div v-else>
          <div class="title">
            {{ theme ? theme.title : '' }}
          </div>
        </div>

        <div class="offline-switch-wrapper">
          <vv-switch
            :value="keepOffline"
            v-model="offline"
            :label="$t('lesson.offline_switch')"
            :big="true"
            :showGeoloc="haveGeoloc"
          />
        </div>
        <div class="titleMobile">
          {{ lesson ? lesson.title : '' }}
        </div>
        <div
          v-if="lesson.videoUrl"
          class="spacer"
          :class="[
            $mq === 'desktop' ? 'video-spacer-desktop' : 'video-spacer',
            offline && noOfflineVideo ? 'no-offline' : ''
          ]"
        >
          <div class="video">
            <div class="offline-container" v-if="keepOffline">
              <video v-if="!noOfflineVideo" :src="offlineSourceVideo" controls="true" />
              <span v-else>No offline video available</span>
            </div>
            <iframe
              v-else
              class="video-ifr"
              :src="lesson.videoUrl"
              frameborder="0"
              allow="autoplay; fullscreen"
              allowfullscreen
            ></iframe>
          </div>
        </div>
        <div
          v-if="lesson.podcastUrl"
          class="spacer"
          :class="[
            $mq === 'desktop' ? 'podcast-spacer-desktop' : 'podcast-spacer',
            offline && noOfflineVideo ? 'no-offline' : ''
          ]"
        >
          <div class="podcast">
            <div class="offline-container" v-if="keepOffline">
              <audio v-if="!noOfflinePodcast" :src="offlineSourcePodcast" controls="true" />
              <span v-else>No offline podcast available</span>
            </div>
            <anchor
              v-else
              :url="lesson.podcastUrl"
              @propagate-offline-download="updatePodcastTrackInfos"
            ></anchor>
          </div>
        </div>
        <div v-if="lesson && lesson.mustKnow && lesson.mustKnow.length > 0" class="spacer">
          <vv-tips :title="lesson.mustKnowTitle" :tips="lesson.mustKnow"></vv-tips>
        </div>
        <div class="spacer">
          <vv-rich-text :document="lesson.content" :center="true" />
        </div>
        <div v-if="lesson && lesson.toRemember && lesson.toRemember.length > 0" class="spacer">
          <vv-tips :title="lesson.toRememberTitle" :tips="lesson.toRemember"></vv-tips>
        </div>
        <div class="cta">
          <vv-button @click="nextLesson">{{
            lesson.buttonLabel || $t('lessonStep.next')
          }}</vv-button>
        </div>
      </div>
    </vv-screen-wrapper>
  </screen-swiper>
</template>

<script>
import { mapGetters } from 'vuex'

import { VvButton, VvRichText, VvTips, VvScreenWrapper, VvSwitch } from '@/components/common'
import Anchor from '@/components/podcast/Anchor'
import ScreenSwiper from '@/components/layout/ScreenSwiper'
import userMixin from '@/mixins/user'
import remoteConfigMixin from '@/mixins/remoteConfig'
import { REGISTER_COMPLETION } from '@/store/modules/completion/types'
import { SET_KEEP_OFFLINE_MODE } from '@/store/modules/user/types'
import VideoManager from '@/lib/video'
import PodcastManager from '@/lib/podcast'
import VvModal from '@/components/common/VvModal.vue'
import { goToNextLessonScreen, goToPrevLessonScreen } from '@/helpers/navigation'

export default {
  name: 'Lesson',
  components: {
    VvButton,
    VvRichText,
    VvTips,
    ScreenSwiper,
    VvScreenWrapper,
    VvSwitch,
    Anchor
  },
  data() {
    return {
      offline: false,
      offlineSourceVideo: null,
      offlineSourcePodcast: null,
      noOfflineVideo: false,
      noOfflinePodcast: false,
      podcastTrackUrl: null,
      podcastTrackId: null,
      seen: false,
      haveGeoloc: true
    }
  },
  mixins: [userMixin, remoteConfigMixin],
  computed: {
    ...mapGetters({
      lessonStepProgression: 'lessonStepProgression',
      firebaseId: 'lessonFirebaseID',
      lesson: 'currentLesson',
      lessonStep: 'currentLessonStep',
      theme: 'currentTheme'
    }),
    canGoBack() {
      if (!this.lesson) return false

      return this.lessonStep.lessons.indexOf(this.lesson.id) > 0
    },
    crumbs() {
      if (localStorage.getItem('triggerModal')) {
        this.showModal()
        localStorage.removeItem('triggerModal')
      }
      if (this.theme && this.lessonStep && this.lesson) {
        return [
          {
            label: this.theme.title,
            value: this.theme.id,
            routing: { name: 'theme', params: { themeId: this.theme.id } }
          },
          {
            label: this.lessonStep.title,
            value: this.lessonStep.id,
            routing: {
              name: 'theme',
              params: { themeId: this.theme.id }
            }
          },
          {
            label: this.lesson.title,
            value: this.lesson.id
          }
        ]
      }

      return []
    },
    keepOffline() {
      return this.$store.state.user.keepOfflineMode
    }
  },
  created() {
    this.offline = this.keepOffline
  },
  async mounted() {
    this.prepareOfflineVideo()
    this.prepareOfflinePodcast()
    if (localStorage.getItem('triggerModal')) {
      this.showModal()
      localStorage.removeItem('triggerModal')
    }
    this.haveGeoloc = this.ifThemeHaveGeoloc()
  },
  methods: {
    showModal() {
      this.$modal.show(
        VvModal,
        {},
        {
          adaptive: true,
          reset: true,
          styles: {
            'border-radius': '20px'
          }
        }
      )
    },
    updatePodcastTrackInfos(value) {
      this.podcastTrackUrl = value.url
      this.podcastTrackId = value.id
    },
    prevLesson() {
      goToPrevLessonScreen(this.lesson.id, this.lessonStep.id, this.theme.id)
    },
    nextLesson() {
      this.$store.dispatch(REGISTER_COMPLETION, {
        contentType: 'lesson',
        id: this.lesson.id,
        status: { complete: true }
      })
      goToNextLessonScreen(this.lesson.id, this.lessonStep.id, this.theme.id, true)
    },
    async prepareOfflineVideo(newLesson) {
      const lesson = newLesson || this.lesson

      this.noOfflineVideo = true
      this.offlineSourceVideo = null

      try {
        const videoId = VideoManager.getVimeoIdFromLesson(lesson)

        const blob = await VideoManager.getVideo(videoId)

        this.offlineSourceVideo = URL.createObjectURL(blob)
        this.noOfflineVideo = false
      } catch (error) {
        this.noOfflineVideo = true
      }
    },
    async prepareOfflinePodcast(newLesson) {
      const lesson = newLesson || this.lesson
      let blob = null
      this.noOfflinePodcast = true
      this.offlineSourcePodcast = null

      try {
        // download podcast
        blob = await PodcastManager.dispatchPodcastDownload(
          this.lesson.podcastUrl,
          this.podcastTrackUrl,
          this.podcastTrackId,
          false
        )
        if (blob) {
          // set sources
          this.offlineSourcePodcast = URL.createObjectURL(blob)
          this.noOfflinePodcast = false
        } else {
          this.noOfflinePodcast = true
        }
      } catch (error) {
        this.noOfflinePodcast = true
      }
    },
    backToMasterclass() {
      const { themeId } = this.$route.params

      this.$router.push({ name: 'theme', params: { themeId } })
    },
    haveData(value) {
      return value != null && value !== ''
    },
    ifThemeHaveGeoloc() {
      return (
        this.haveData(this.theme.coordinatesWineEstate) || this.haveData(this.theme.geojsonTrack)
      )
    }
  },
  watch: {
    async lesson(newLesson) {
      this.prepareOfflineVideo(newLesson)
      this.prepareOfflinePodcast(newLesson)
    },
    async podcastTrackUrl() {
      this.prepareOfflinePodcast()
    },
    offline(value) {
      this.$store.commit(SET_KEEP_OFFLINE_MODE, value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';
@import '@/assets/styles/responsive.scss';

.buttonClass {
  display: flex;
  flex-direction: row;
}
.backButton {
  margin-right: 35px;
  cursor: pointer;
  background-color: #ffffff;
  height: 55px;
  width: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  box-shadow: 3px 3px 3px #bebebe, -4px -4px 4px #ffffff;
  img {
    height: 60%;
    width: 60%;
    margin-right: 4px;
    transform: rotate(180deg);
  }
  &:hover {
    box-shadow: -3px -3px 4px #bebebe, 3px 3px 2px #ffffff;
  }
}
.qrcodeBtn {
  height: 35px;
  padding: 8px;
  margin: 5px;
  border-radius: 50px;
  img {
    height: 100%;
    width: 100%;
  }
  cursor: pointer;
}
.geolocBtn {
  height: 40px;
  padding: 5px 12px;
  margin: 5px;
  border-radius: 50px;
  img {
    height: 100%;
    width: 100%;
  }
  cursor: pointer;
}
.leftButton {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.title {
  text-align: center;
  font-weight: bold;
  font-size: 20px;
  letter-spacing: 0px;
  color: #000000;
}
.titleMobile {
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 0px;
  color: #000000;
  margin: 15px 0;
}
.boxTitle {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 10px;
  margin-top: 10px;
}
.screen-container {
  padding-top: 6%;
}

.offline-switch-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}

.video-spacer,
.podcast-spacer {
  max-width: 350px;
  margin: 30px auto;
  margin-top: 0;
}

.video-spacer-desktop,
.podcast-spacer-desktop {
  margin: 30px auto;
}

.video {
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  height: 50vh;
}

.video iframe,
.video object,
.video embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.video-spacer .video {
  height: 35vh;
}

.podcast iframe,
.podcast object,
.podcast embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.video,
.podcast {
  .offline-container {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    video {
      max-width: 100%;
      height: 100%;
    }
  }
}

.spacer {
  margin-bottom: 20px;
  &.bread {
    margin-top: 30px;
    margin-bottom: 44px;
    position: relative;

    @include for-size(desktop-up) {
      left: -30px;
    }
  }
}

.cta {
  bottom: 10px !important;

  // Comment to get old values for padding 10px/38px
  button {
    padding: 12px 50px !important;
  }
}

.no-offline {
  height: unset;

  .video,
  .podcast {
    height: unset;
  }
}
</style>
